import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { gearApiClient } from '../../gearApiClient';
import { gearIcons, getGearLabel } from '../../gears';
import type { GearInventory, GearInventoryRequest } from '../../models/GearInventory';
import type { GearItem, GearItemRequest } from '../../models/GearItem';
import { NotificationSnackbar, type SnackbarSeverity } from '../../components/common/NotificationSnackbar';

type ItemFormState = {
  gearType: string;
  details: string;
  availableCount: string;
  pricePerDay: string;
  currencyCode: string;
};

const emptyItemForm = (currencyCode = 'PLN'): ItemFormState => ({
  gearType: '',
  details: '',
  availableCount: '1',
  pricePerDay: '',
  currencyCode,
});

const formatMoney = (amount: number, currencyCode: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
};

const HostGearInventoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [inventory, setInventory] = useState<GearInventory | null>(null);
  const [items, setItems] = useState<GearItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingInventory, setSavingInventory] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [archiveBusyId, setArchiveBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GearItem | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<GearItem | null>(null);
  const [inventoryForm, setInventoryForm] = useState<GearInventoryRequest>({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zipCode: '',
    countryCode: '',
  });
  const [itemForm, setItemForm] = useState<ItemFormState>(emptyItemForm());

  const gearTypeOptions = useMemo(
    () => Object.keys(gearIcons).sort((left, right) => getGearLabel(left).localeCompare(getGearLabel(right))),
    [],
  );

  const showSnackbar = useCallback((message: string, severity: SnackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const applyInventoryState = useCallback((nextInventory: GearInventory | null, nextItems?: GearItem[]) => {
    setInventory(nextInventory);
    setItems(nextItems ?? nextInventory?.items ?? []);
    setInventoryForm({
      name: nextInventory?.name ?? '',
      addressLine1: nextInventory?.addressLine1 ?? '',
      addressLine2: nextInventory?.addressLine2 ?? '',
      city: nextInventory?.city ?? '',
      zipCode: nextInventory?.zipCode ?? '',
      countryCode: nextInventory?.countryCode ?? '',
    });
  }, []);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextInventory = await gearApiClient.getHostGearInventory();
      applyInventoryState(nextInventory, nextInventory?.items ?? []);
    } catch {
      setError(t('hostGearInventory.loadError', 'Could not load gear inventory.'));
    } finally {
      setLoading(false);
    }
  }, [applyInventoryState, t]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const resetItemDialog = useCallback((item?: GearItem | null) => {
    setEditingItem(item ?? null);
    setItemForm(
      item
        ? {
            gearType: item.gearType,
            details: item.details ?? '',
            availableCount: String(item.availableCount),
            pricePerDay: String(item.pricePerDay),
            currencyCode: item.currencyCode,
          }
        : emptyItemForm(),
    );
    setItemDialogOpen(true);
  }, []);

  const closeItemDialog = useCallback(() => {
    setItemDialogOpen(false);
    setEditingItem(null);
    setItemForm(emptyItemForm());
  }, []);

  const handleInventoryChange = (field: keyof GearInventoryRequest, value: string) => {
    setInventoryForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleSaveInventory = async () => {
    const trimmedRequest: GearInventoryRequest = {
      name: inventoryForm.name.trim(),
      addressLine1: inventoryForm.addressLine1.trim(),
      addressLine2: inventoryForm.addressLine2?.trim() || '',
      city: inventoryForm.city.trim(),
      zipCode: inventoryForm.zipCode?.trim() || '',
      countryCode: inventoryForm.countryCode.trim().toUpperCase(),
    };

    if (!trimmedRequest.name || !trimmedRequest.addressLine1 || !trimmedRequest.city || !trimmedRequest.countryCode) {
      showSnackbar(t('hostGearInventory.inventoryValidationError', 'Name, address line 1, city, and country code are required.'), 'error');
      return;
    }

    try {
      setSavingInventory(true);
      const savedInventory = await gearApiClient.upsertHostGearInventory(trimmedRequest);
      applyInventoryState(savedInventory, savedInventory.items ?? items);
      showSnackbar(t('hostGearInventory.inventorySaved', 'Inventory saved successfully.'), 'success');
    } catch {
      showSnackbar(t('hostGearInventory.inventorySaveError', 'Could not save inventory.'), 'error');
    } finally {
      setSavingInventory(false);
    }
  };

  const handleItemFieldChange = (field: keyof ItemFormState, value: string) => {
    setItemForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleSaveItem = async () => {
    const gearType = itemForm.gearType.trim();
    const availableCount = Number(itemForm.availableCount);
    const pricePerDay = Number(itemForm.pricePerDay);
    const currencyCode = itemForm.currencyCode.trim().toUpperCase();

    if (!gearType || !Number.isFinite(availableCount) || availableCount < 1 || !Number.isFinite(pricePerDay) || pricePerDay < 0 || !currencyCode) {
      showSnackbar(t('hostGearInventory.itemValidationError', 'Choose a gear type and enter a valid count, price, and currency code.'), 'error');
      return;
    }

    const request: GearItemRequest = {
      gearType,
      details: itemForm.details.trim() || null,
      availableCount: Math.trunc(availableCount),
      pricePerDay,
      currencyCode,
    };

    try {
      setSavingItem(true);
      const savedItem = editingItem
        ? await gearApiClient.updateHostGearItem(editingItem.id, request)
        : await gearApiClient.createHostGearItem(request);

      setItems((previous) => {
        const nextItems = previous.filter((current) => current.id !== savedItem.id);
        return [savedItem, ...nextItems].sort((left, right) => getGearLabel(left.gearType).localeCompare(getGearLabel(right.gearType)));
      });
      closeItemDialog();
      showSnackbar(
        editingItem
          ? t('hostGearInventory.itemUpdated', 'Item updated successfully.')
          : t('hostGearInventory.itemCreated', 'Item added successfully.'),
        'success',
      );
    } catch {
      showSnackbar(t('hostGearInventory.itemSaveError', 'Could not save item.'), 'error');
    } finally {
      setSavingItem(false);
    }
  };

  const handleArchiveItem = async () => {
    if (!archiveTarget) return;

    try {
      setArchiveBusyId(archiveTarget.id);
      await gearApiClient.archiveHostGearItem(archiveTarget.id);
      setItems((previous) => previous.filter((item) => item.id !== archiveTarget.id));
      setArchiveTarget(null);
      showSnackbar(t('hostGearInventory.itemArchived', 'Item archived.'), 'success');
    } catch {
      showSnackbar(t('hostGearInventory.itemArchiveError', 'Could not archive item.'), 'error');
    } finally {
      setArchiveBusyId(null);
    }
  };

  const renderGearType = (gearType: string) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {gearIcons[gearType as keyof typeof gearIcons] ?? gearIcons.CUSTOM}
      <span>{getGearLabel(gearType)}</span>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1280, width: '100%', mx: 'auto' }}>
      <Stack spacing={3}>
        <Card elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Inventory2Icon color="primary" />
              <Box>
                <Typography variant="h4" component="h1">
                  {t('hostGearInventory.title', 'Gear inventory')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('hostGearInventory.subtitle', 'Manage your organization address and the gear available for rental.')}
                </Typography>
              </Box>
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}

            {!loading && !error && !inventory ? (
              <Alert severity="info">
                {t(
                  'hostGearInventory.noInventoryYet',
                  'No gear inventory yet. Save your pickup address below to create one, then add gear items.',
                )}
              </Alert>
            ) : null}

            <Divider />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
            <Stack spacing={2}>
              <Typography variant="h6">{t('hostGearInventory.inventoryTitle', 'Inventory details')}</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label={t('hostGearInventory.nameLabel', 'Inventory name')}
                  value={inventoryForm.name}
                  onChange={(event) => handleInventoryChange('name', event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('hostGearInventory.countryCodeLabel', 'Country code')}
                  value={inventoryForm.countryCode}
                  onChange={(event) => handleInventoryChange('countryCode', event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('hostGearInventory.addressLine1Label', 'Address line 1')}
                  value={inventoryForm.addressLine1}
                  onChange={(event) => handleInventoryChange('addressLine1', event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('hostGearInventory.addressLine2Label', 'Address line 2')}
                  value={inventoryForm.addressLine2 ?? ''}
                  onChange={(event) => handleInventoryChange('addressLine2', event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('hostGearInventory.cityLabel', 'City')}
                  value={inventoryForm.city}
                  onChange={(event) => handleInventoryChange('city', event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('hostGearInventory.zipCodeLabel', 'ZIP code')}
                  value={inventoryForm.zipCode ?? ''}
                  onChange={(event) => handleInventoryChange('zipCode', event.target.value)}
                  fullWidth
                />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSaveInventory}
                  disabled={savingInventory}
                  startIcon={savingInventory ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {inventory
                    ? t('common.save', 'Save')
                    : t('hostGearInventory.createInventory', 'Create inventory')}
                </Button>
              </Stack>
            </Stack>
            )}
          </Stack>
        </Card>

        <Card elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6">{t('hostGearInventory.itemsTitle', 'Gear items')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('hostGearInventory.itemsSubtitle', 'Set quantity, pricing, and status for each gear type.')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => resetItemDialog(null)}
                disabled={!inventory || loading}
              >
                {t('hostGearInventory.addItem', 'Add item')}
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : !inventory ? (
              <Alert severity="info">
                {t(
                  'hostGearInventory.itemsNeedInventory',
                  'Save inventory details first to start adding gear items.',
                )}
              </Alert>
            ) : items.length === 0 ? (
              <Alert severity="info">{t('hostGearInventory.noItems', 'No gear items yet. Add one to start renting gear.')}</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
                <Table sx={{ minWidth: 920 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('hostGearInventory.col.gearType', 'Gear type')}</TableCell>
                      <TableCell>{t('hostGearInventory.col.details', 'Details')}</TableCell>
                      <TableCell align="right">{t('hostGearInventory.col.availableCount', 'Available')}</TableCell>
                      <TableCell align="right">{t('hostGearInventory.col.pricePerDay', 'Price / day')}</TableCell>
                      <TableCell>{t('hostGearInventory.col.status', 'Status')}</TableCell>
                      <TableCell align="right">{t('hostGearInventory.col.actions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => {
                      const statusColor = item.status === 'ACTIVE' ? 'success' : 'default';

                      return (
                        <TableRow key={item.id} hover>
                          <TableCell>{renderGearType(item.gearType)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.details || t('common.noResults', 'No details')}</Typography>
                          </TableCell>
                          <TableCell align="right">{item.availableCount}</TableCell>
                          <TableCell align="right">{formatMoney(item.pricePerDay, item.currencyCode)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={statusColor}
                              label={t(`hostGearInventory.status.${item.status}`, item.status)}
                              variant={item.status === 'ARCHIVED' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" startIcon={<EditIcon />} onClick={() => resetItemDialog(item)}>
                                {t('common.edit', 'Edit')}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setArchiveTarget(item)}
                                disabled={archiveBusyId === item.id}
                              >
                                {t('common.delete', 'Delete')}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Card>
      </Stack>

      <Dialog open={itemDialogOpen} onClose={closeItemDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingItem
            ? t('hostGearInventory.editItemTitle', 'Edit item')
            : t('hostGearInventory.addItemTitle', 'Add item')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label={t('hostGearInventory.gearTypeLabel', 'Gear type')}
              value={itemForm.gearType}
              onChange={(event) => handleItemFieldChange('gearType', event.target.value)}
              fullWidth
            >
              {gearTypeOptions.map((gearType) => (
                <MenuItem key={gearType} value={gearType}>
                  {getGearLabel(gearType)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('hostGearInventory.detailsLabel', 'Details')}
              value={itemForm.details}
              onChange={(event) => handleItemFieldChange('details', event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                type="number"
                label={t('hostGearInventory.availableCountLabel', 'Available count')}
                value={itemForm.availableCount}
                onChange={(event) => handleItemFieldChange('availableCount', event.target.value)}
                inputProps={{ min: 1, step: 1 }}
                fullWidth
              />
              <TextField
                type="number"
                label={t('hostGearInventory.pricePerDayLabel', 'Price / day')}
                value={itemForm.pricePerDay}
                onChange={(event) => handleItemFieldChange('pricePerDay', event.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
              />
              <TextField
                label={t('hostGearInventory.currencyCodeLabel', 'Currency code')}
                value={itemForm.currencyCode}
                onChange={(event) => handleItemFieldChange('currencyCode', event.target.value)}
                fullWidth
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeItemDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveItem}
            disabled={savingItem}
            startIcon={savingItem ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)}>
        <DialogTitle>{t('hostGearInventory.archiveTitle', 'Archive item')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('hostGearInventory.archiveMessage', 'Archive this item? It will no longer be available for new rentals.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleArchiveItem} disabled={!archiveTarget || archiveBusyId === archiveTarget.id}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default HostGearInventoryPage;